import { useParams, Link } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BreadcrumbsJsonLd from '../components/BreadcrumbsJsonLd';
import OrganizationJsonLd from '../components/OrganizationJsonLd';
import { 
  ArrowLeft, 
  Share2, 
  Twitter, 
  Linkedin, 
  ShieldCheck, 
  Clock, 
  Calendar, 
  User, 
  FileText, 
  ChevronRight, 
  Mail, 
  Users,
  Quote,
  Award
} from 'lucide-react';
import { researchPapers } from '../data/researchPapers';
import Breadcrumbs from '../components/Breadcrumbs';

// JSON-LD Component for ScholarlyArticle - Essential for indexing as research
const ScholarlyJsonLd = ({ paper }: { paper: any }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ScholarlyArticle",
    "headline": paper.title,
    "image": `https://greentick.org.in/og-${paper.id}.jpg`,
    "author": {
      "@type": "Organization",
      "name": "GreenTick Research Lab",
      "url": "https://greentick.org.in/research"
    },
    "publisher": {
      "@type": "Organization",
      "name": "GreenTick AI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://greentick.org.in/image.png"
      }
    },
    "datePublished": paper.date,
    "description": paper.abstract,
    "keywords": paper.keywords,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://greentick.org.in/research/${paper.id}`
    }
  };

  return (
    <script type="application/ld+json">
      {JSON.stringify(schema)}
    </script>
  );
};

export default function ResearchPaperDetail() {
  const { slug } = useParams();
  const paper = researchPapers.find(p => p.id === slug);
  
  useSEO({
    title: `${paper?.title || 'Research Paper'} | GreenTick Lab`,
    description: paper?.abstract || 'Technical research into AI-driven behavioral hiring and skill verification.',
    keywords: paper?.keywords,
    canonicalPath: `/research/${slug}`,
    ogType: 'article'
  });

  if (!paper) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4 tracking-tighter">404</h1>
          <p className="text-slate-400 mb-8 italic">Research methodology not indexed.</p>
          <Link to="/research" className="text-teal-400 hover:text-teal-300 flex items-center gap-2 justify-center font-bold uppercase tracking-widest text-xs">
            <ArrowLeft className="w-4 h-4" /> Back to Research Lab
          </Link>
        </div>
      </div>
    );
  }

  const handleCopyCitation = () => {
    const citation = `${paper.author}. (${paper.date}). ${paper.title}. GreenTick Research Lab Whitepaper. Available at: https://greentick.org.in/research/${paper.id}`;
    navigator.clipboard.writeText(citation);
    alert('Academic citation copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-slate-950 selection:bg-teal-500/30">
      <ScholarlyJsonLd paper={paper} />
      <OrganizationJsonLd />
      <Navbar />
      
      {paper && (
        <BreadcrumbsJsonLd 
          items={[
            { name: 'Home', item: '/' },
            { name: 'Research Lab', item: '/research' },
            { name: paper.title, item: `/research/${slug}` }
          ]}
        />
      )}
      
      {/* Journal Header Section */}
      <header className="pt-32 pb-20 border-b border-white/5 bg-slate-900/40 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          {/* Breadcrumbs for SEO & UX */}
          <Breadcrumbs 
            items={[
              { name: 'Technical Archive', item: '/research' },
              { name: paper.title, item: `/research/${slug}` }
            ]} 
          />

          <Link to="/research" className="inline-flex items-center gap-2 text-slate-400 hover:text-teal-400 transition-colors mb-12 group">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-xs font-bold uppercase tracking-widest">GreenTick Technical Archive</span>
          </Link>
          
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
            <h1 className="text-4xl md:text-6xl font-bold text-white leading-[1.1] max-w-4xl tracking-tight">
              {paper.title}
            </h1>
            <div className="flex gap-4">
              <button onClick={() => window.print()} className="p-4 bg-white/5 rounded-2xl border border-white/10 text-slate-400 hover:text-white hover:bg-white/10 transition-all" title="Print/Save as PDF" aria-label="Print or Save as PDF">
                <FileText className="w-5 h-5" />
              </button>
              <button onClick={handleCopyCitation} className="p-4 bg-white/5 rounded-2xl border border-white/10 text-slate-400 hover:text-white hover:bg-white/10 transition-all" title="Share/Cite" aria-label="Copy Academic Citation">
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex wrap gap-10 text-slate-400 border-t border-white/5 pt-10">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-teal-500/10 flex items-center justify-center border border-teal-500/20">
                <User className="w-4 h-4 text-teal-500" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">Principal Author</div>
                <span className="text-sm text-white font-medium">{paper.author}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-teal-500/10 flex items-center justify-center border border-teal-500/20">
                <Calendar className="w-4 h-4 text-teal-500" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">Published</div>
                <span className="text-sm text-white font-medium">{paper.date}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-teal-500/10 flex items-center justify-center border border-teal-500/20">
                <Clock className="w-4 h-4 text-teal-500" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">Reading Time</div>
                <span className="text-sm text-white font-medium">{paper.readTime}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Journal Layout */}
      <main className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-20">
          
          {/* Metadata Sidebar (Left) */}
          <aside className="lg:col-span-4 lg:pr-8">
            <div className="sticky top-32 space-y-12">
              
              {/* Paper Status & DOI */}
              <div className="space-y-6">
                <div className="p-8 glass-dark rounded-[32px] border border-white/5">
                   <div className="flex items-center gap-3 mb-6">
                      <ShieldCheck className="w-5 h-5 text-teal-400" />
                      <span className="text-xs font-bold text-white uppercase tracking-widest">Internal Peer Review</span>
                   </div>
                   <div className="space-y-4">
                      <div>
                         <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1">Electronic DOI</div>
                         <div className="text-xs font-mono text-teal-500/70 truncate">10.GTL.ARCHIVE/{paper.id.toUpperCase().replace(/-/g, '')}</div>
                      </div>
                      <div>
                         <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1">Confidence Score</div>
                         <div className="flex items-center gap-2">
                            <div className="h-1.5 flex-1 bg-slate-800 rounded-full overflow-hidden">
                               <div className="h-full bg-teal-500 w-[96%]"></div>
                            </div>
                            <span className="text-xs font-bold text-white">96%</span>
                         </div>
                      </div>
                   </div>
                </div>
              </div>

              {/* Technical Indicators */}
              <div className="space-y-6">
                <h3 className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-400 px-2 italic">Quantitative Synthesis</h3>
                <div className="overflow-hidden">
                  <table className="w-full text-left border-collapse">
                    <caption className="sr-only">Quantitative Synthesis KPIs for {paper.title}</caption>
                    <tbody className="divide-y divide-white/5">
                      {paper.dataSynthesis.kpis.map(kpi => (
                        <tr key={kpi.label} className="group hover:bg-white/5 transition-colors">
                          <td className="py-4 pr-4">
                            <div className="text-[9px] text-slate-400 font-bold uppercase tracking-widest leading-none mb-1">{kpi.label}</div>
                            <div className="text-2xl font-bold text-white tracking-tighter">{kpi.value}</div>
                          </td>
                          <td className="py-4 text-right">
                            <div className={`inline-block w-2 h-2 rounded-full ${kpi.trend === 'up' ? 'bg-teal-500 animate-pulse' : 'bg-teal-500'}`}></div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* References Preview */}
              <div className="space-y-6 pt-12 border-t border-white/5">
                 <h3 className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-400 px-2 italic">Key References</h3>
                 <div className="space-y-4">
                    {paper.references.slice(0, 2).map((ref, i) => (
                      <div key={i} className="flex gap-3">
                         <div className="text-[10px] font-bold text-teal-500/40 mt-1">[{i+1}]</div>
                         <p className="text-[10px] text-slate-400 leading-relaxed font-serif italic">{ref}</p>
                      </div>
                    ))}
                 </div>
              </div>

              {/* Social Connect */}
              <div className="flex gap-3 pt-8">
                  <button className="flex-1 flex justify-center py-3 bg-white/5 rounded-xl border border-white/10 text-slate-400 hover:text-white transition-all" aria-label="Share on Twitter">
                    <Twitter className="w-4 h-4" />
                  </button>
                  <button className="flex-1 flex justify-center py-3 bg-white/5 rounded-xl border border-white/10 text-slate-400 hover:text-white transition-all" aria-label="Share on LinkedIn">
                    <Linkedin className="w-4 h-4" />
                  </button>
              </div>

            </div>
          </aside>

          {/* Article Body (Right) */}
          <article className="lg:col-span-8">
            <div className="blog-content max-w-none">
              
              {/* Abstract Section */}
              <section className="mb-24">
                <div className="flex items-center gap-4 mb-8">
                   <div className="h-[1px] flex-1 bg-gradient-to-r from-teal-500/5 to-teal-500/50"></div>
                   <span className="text-[10px] font-bold uppercase tracking-[0.5em] text-teal-500 italic">Technical Abstract</span>
                   <div className="h-[1px] w-12 bg-teal-500/50"></div>
                </div>
                <p className="article-abstract text-2xl md:text-3xl text-slate-200 font-serif leading-relaxed italic border-l-2 border-teal-500/40 pl-10 pr-4">
                  "{paper.abstract}"
                </p>
              </section>

              {/* Introduction */}
              <section className="mb-20">
                <h2 className="text-4xl font-bold text-white mb-8 flex items-center gap-4">
                   <span className="text-teal-500/20 font-sans text-5xl">01</span>
                   Introduction
                </h2>
                <div className="space-y-6">
                  {paper.introduction.split('\n\n').map((p, i) => (
                    <p key={i} className="text-lg text-slate-300 font-serif leading-relaxed">
                      {p}
                    </p>
                  ))}
                </div>
              </section>

              {/* Methodology Section */}
              <section className="mb-24">
                <div className="p-1 border-b border-white/5 mb-12">
                   <h2 className="text-4xl font-bold text-white flex items-center gap-4 mb-4">
                      <span className="text-teal-500/20 font-sans text-5xl">02</span>
                      Methodology
                   </h2>
                   <p className="text-sm font-bold text-teal-500 uppercase tracking-widest pl-16 mb-6">— {paper.methodology.title}</p>
                </div>
                
                <p className="text-lg text-slate-300 font-serif leading-relaxed mb-12 pl-16">
                  {paper.methodology.content}
                </p>

                {paper.methodology.metrics && (
                   <div className="ml-16 overflow-hidden">
                      <table className="w-full text-left border-collapse">
                        <caption className="sr-only">Methodological Metrics for {paper.title}</caption>
                        <thead>
                          <tr className="border-b border-white/10 text-[10px] font-bold uppercase tracking-widest text-slate-400 italic">
                             <th scope="col" className="py-4 font-bold">Empirical Metric</th>
                             <th scope="col" className="py-4 font-bold text-right">Verification Value</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {paper.methodology.metrics.map((m, idx) => (
                            <tr key={idx} className="group hover:bg-white/5 transition-colors">
                              <td className="py-8 pr-12">
                                <div className="text-white font-bold text-lg mb-2">{m.label}</div>
                                <div className="text-[11px] text-slate-400 leading-relaxed font-sans uppercase tracking-wider italic font-medium">{m.description}</div>
                              </td>
                              <td className="py-8 text-right align-top">
                                <div className="text-5xl font-bold text-white tracking-tighter group-hover:text-teal-400 transition-colors">
                                  {m.value}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                   </div>
                )}
              </section>

              {/* Data Insights Panel */}
              <section className="mb-24">
                <div className="relative p-12 bg-slate-900/50 rounded-[48px] border border-white/5 overflow-hidden group">
                   <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-teal-500/5 blur-[100px] pointer-events-none"></div>
                   <div className="relative z-10">
                      <h2 className="text-2xl font-bold text-white mb-6 font-serif underline decoration-teal-500/30 underline-offset-8">
                         {paper.dataSynthesis.title}
                      </h2>
                      <p className="text-lg text-slate-300 font-serif leading-relaxed italic mb-8">
                        {paper.dataSynthesis.content}
                      </p>
                      <div className="flex items-center gap-4 text-xs font-bold text-teal-500 uppercase tracking-widest">
                         <Award className="w-4 h-4" />
                         Dataset: 50,000+ Interactions Verified
                      </div>
                   </div>
                </div>
              </section>

              {/* Industrial Application */}
              <section className="mb-24">
                 <h2 className="text-4xl font-bold text-white mb-12 flex items-center gap-4">
                    <span className="text-teal-500/20 font-sans text-5xl">03</span>
                    Industrial Application
                 </h2>
                 
                 <div className="p-10 bg-white/5 border border-white/10 rounded-[40px] relative">
                    <div className="absolute -top-4 -left-4 p-4 bg-teal-500 rounded-2xl shadow-xl shadow-teal-500/20">
                       <Quote className="w-6 h-6 text-white" />
                    </div>
                    <div className="space-y-8">
                       <div>
                          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-[0.3em] mb-4 italic">Case Environment: {paper.caseStudy.title}</h4>
                          <p className="text-xl text-white font-serif italic leading-relaxed">"{paper.caseStudy.context}"</p>
                       </div>
                       <div className="pt-8 border-t border-white/5">
                          <h4 className="text-xs font-bold text-teal-500 uppercase tracking-[0.3em] mb-4 italic">Quantified Outcome</h4>
                          <p className="text-lg text-slate-400 font-serif leading-relaxed italic">{paper.caseStudy.result}</p>
                       </div>
                    </div>
                 </div>
              </section>

              {/* Bibliography */}
              <section className="mt-32 pt-16 border-t border-white/10">
                 <h3 className="text-[10px] font-bold uppercase tracking-[0.5em] text-slate-600 mb-10 italic">Technical Bibliography & Citations</h3>
                 <div className="space-y-6">
                    {paper.references.map((ref, idx) => (
                      <div key={idx} className="flex gap-6 items-start group">
                         <span className="text-[10px] font-bold text-teal-800 group-hover:text-teal-500 transition-colors">[{idx+1}]</span>
                         <p className="text-[11px] text-slate-600 font-serif italic leading-loose group-hover:text-slate-400 transition-colors">
                            {ref}
                         </p>
                      </div>
                    ))}
                 </div>
              </section>

            </div>
          </article>
        </div>
      </main>

      {/* Conversion Section */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <a
            href={`mailto:sandesh@greentick.org.in?subject=Technical Briefing Request: ${paper.id}&body=Hi Sandesh,%0D%0A%0D%0AI was reading the research paper on '${paper.title}' and would like to request a deep-dive technical briefing for our organization.%0D%0A%0D%0AOrganization Name: %0D%0ATarget Role Volume: `}
            className="group p-12 glass rounded-[48px] border border-white/5 hover:border-teal-500/40 transition-all flex flex-col items-center text-center relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-b from-teal-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="p-5 bg-teal-500/10 rounded-[28px] mb-8 group-hover:scale-110 transition-transform relative z-10">
              <Mail className="w-8 h-8 text-teal-500" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4 relative z-10 tracking-tight">Technical Briefing</h3>
            <p className="text-slate-400 text-sm italic mb-8 relative z-10 max-w-xs">Access the full confidential dataset and logic-chain implementation roadmap.</p>
            <div className="mt-auto flex items-center gap-2 text-teal-400 font-bold text-xs uppercase tracking-widest relative z-10 underline decoration-teal-500/20 underline-offset-8 group-hover:decoration-teal-500 transition-all">
              Request Full Framework <ChevronRight className="w-4 h-4" />
            </div>
          </a>

          <a
            href={`mailto:sandesh@greentick.org.in?subject=Advisory Board Inquiry: Research Lab&body=Hi Sandesh,%0D%0A%0D%0AI'm interested in joining the GreenTick Research Advisory Board. I have experience in [Core Experience] and would like to contribute to future behavioral hiring studies.`}
            className="group p-12 glass rounded-[48px] border border-white/5 hover:border-teal-500/40 transition-all flex flex-col items-center text-center relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-b from-teal-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="p-5 bg-teal-500/10 rounded-[28px] mb-8 group-hover:scale-110 transition-transform relative z-10">
              <Users className="w-8 h-8 text-teal-500" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4 relative z-10 tracking-tight">Advisory Board</h3>
            <p className="text-slate-400 text-sm italic mb-8 relative z-10 max-w-xs">Partner with the GreenTick Lab to shape the next generation of behavioral capture.</p>
            <div className="mt-auto flex items-center gap-2 text-teal-400 font-bold text-xs uppercase tracking-widest relative z-10 underline decoration-teal-500/20 underline-offset-8 group-hover:decoration-teal-500 transition-all">
              Apply for Advisory <ChevronRight className="w-4 h-4" />
            </div>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
